import React, { useState, useCallback } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Grid, { GridItem } from '@/components/layout/Grid';
import Botao from '@/components/Botao';
import Card from '@/components/layout/Card';
import Subtitulo from '@/components/tipografia/Subtitulo';
import Modal from '@/components/layout/Modal';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoMascara from '@/components/Formulario/CampoMascara';
import AlertBox from '@/components/layout/AlertBox';
import styles from './page.module.css';
import {
  mdiCheck,
  mdiClose,
  mdiRocketLaunch,
  mdiStarOutline,
  mdiAccountMultiple,
  mdiMessageText,
  mdiBell,
  mdiRobot,
  mdiApi,
  mdiChartLine,
  mdiOfficeBuilding,
  mdiInformationOutline,
  mdiMagnify,
  mdiLoading,
  mdiCash,
  mdiRocket,
  mdiTrendingDown,
  mdiAlertCircleOutline,
} from '@mdi/js';
import Icon from '@/components/Icon';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

const Page = () => {
  const [upgradeTo, setUpgradeTo] = useState<'start' | 'pro' | 'enterprise' | null>(null);
  const upgradeToLabel = upgradeTo ? upgradeTo.charAt(0).toUpperCase() + upgradeTo.slice(1) : '';
  const [emailFinanceiro, setEmailFinanceiro] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [isBuscandoCNPJ, setIsBuscandoCNPJ] = useState(false);
  const [dadosEndereco, setDadosEndereco] = useState<any>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Estados para downgrade para Free
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [showDowngradeInelegivel, setShowDowngradeInelegivel] = useState(false);
  const [downgradeElegibilidade, setDowngradeElegibilidade] = useState<any>(null);
  const [isVerificandoElegibilidade, setIsVerificandoElegibilidade] = useState(false);

  // Query para buscar dados da organização
  const queryOrganizacao = useQuery({
    queryKey: 'organizacao/detalhes',
    queryFn: async () => {
      const response = await axios.get('/api/organizacao/detalhes', {
        withCredentials: true,
      });
      return response.data.result;
    },
    onSuccess: (data) => {
      // Preencher e-mail administrativo como financeiro por padrão
      if (data?.email_administrativo) {
        const primeiroEmail = data.email_administrativo.split(',')[0]?.trim();
        setEmailFinanceiro(primeiroEmail || '');
      }

      if (data?.email) {
        const primeiroEmail = data.email.split(',')[0]?.trim();
        setEmailFinanceiro(primeiroEmail || '');
      }
      // Preencher CNPJ se existir
      if (data?.cnpj) {
        setCnpj(data.cnpj);
      }
    },
  });

  const planoAtual = queryOrganizacao.data?.plano?.nome || 'Free';

  const handleUpgradeClick = (plano: 'start' | 'pro' | 'enterprise') => {
    setUpgradeTo(plano);
  };

  // Função para buscar dados do CNPJ
  const buscarDadosCNPJ = useCallback(async (cnpjValue: string) => {
    if (!cnpjValue || cnpjValue.replace(/\D/g, '').length !== 14) {
      return;
    }

    setIsBuscandoCNPJ(true);
    try {
      const cnpjLimpo = cnpjValue.replace(/\D/g, '');
      const response = await axios.get(`/api/organizacao/busca/cnpj/${cnpjLimpo}`, {
        withCredentials: true
      });

      if (response.data && response.data.ok) {
        const dados = response.data.result;
        setDadosEndereco(dados);
        toast('Dados da empresa encontrados!', {
          toastId: 'auto-cnpj-success',
          position: 'top-center',
          type: 'success',
          autoClose: 3000,
        });
      } else {
        toast(response.data?.msg || 'CNPJ não encontrado ou inválido', {
          toastId: 'auto-cnpj-error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      }
    } catch (error: any) {
      console.error('Erro ao buscar dados do CNPJ:', error);
      const message = error.response?.data?.msg || 'Erro ao buscar dados do CNPJ. Verifique se o CNPJ está correto.';
      toast(message, {
        toastId: 'auto-cnpj-error',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
    } finally {
      setIsBuscandoCNPJ(false);
    }
  }, []);

  // Mutation para realizar o upgrade
  const upgradeMutation = useMutation(
    async () => {
      const response = await axios.post('/api/organizacao/upgrade', {
        ...dadosEndereco,
        email: emailFinanceiro,
        cnpj: cnpj,
        plano: upgradeTo,
      }, {
        withCredentials: true,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        const isEnterprise = data?.result?.status_inscricao === 'aguardando_contato_comercial';
        const successMsg = isEnterprise
          ? 'Solicitação para o plano Enterprise registrada! Nossa equipe comercial entrará em contato em breve.'
          : 'Solicitação de upgrade enviada com sucesso! Em breve receberá o contrato via e-mail.';
        toast(successMsg, {
          toastId: 'upgrade-success',
          position: 'top-center',
          type: 'success',
          autoClose: isEnterprise ? 8000 : 5000,
        });
        setUpgradeTo(null);
        // Recarregar dados da organização
        queryOrganizacao.refetch();
      },
      onError: (error: any) => {
        const message = error.response?.data?.msg || 'Erro ao processar upgrade';
        toast(message, {
          toastId: 'upgrade-error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    }
  );

  const handleConfirmUpgrade = () => {
    // Validação básica
    const errors: Record<string, string> = {};

    if (!emailFinanceiro || !emailFinanceiro.includes('@')) {
      errors.email = 'E-mail financeiro inválido';
    }

    if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) {
      errors.cnpj = 'CNPJ inválido';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    upgradeMutation.mutate();
  };

  // Verifica elegibilidade e abre o modal adequado
  const handleDowngradeFreeClick = async () => {
    setIsVerificandoElegibilidade(true);
    try {
      const resp = await axios.get('/api/organizacao/downgrade-free/elegibilidade', { withCredentials: true });
      const result = resp.data?.result;
      setDowngradeElegibilidade(result);
      if (result?.elegivel) {
        setShowDowngradeConfirm(true);
      } else {
        setShowDowngradeInelegivel(true);
      }
    } catch (err: any) {
      toast(err.response?.data?.msg || 'Erro ao verificar elegibilidade', { type: 'error', position: 'top-center', autoClose: 5000 });
    } finally {
      setIsVerificandoElegibilidade(false);
    }
  };

  // Executa o downgrade para Free
  const downgradeMutation = useMutation(
    async () => {
      const response = await axios.post('/api/organizacao/downgrade-free', {}, { withCredentials: true });
      return response.data;
    },
    {
      onSuccess: () => {
        toast('Plano alterado para Free com sucesso!', { type: 'success', position: 'top-center', autoClose: 5000, toastId: 'downgrade-success' });
        setShowDowngradeConfirm(false);
        queryOrganizacao.refetch();
      },
      onError: (error: any) => {
        const data = error.response?.data;
        // Se vier comparativo de inelegibilidade, mostra o modal detalhado
        if (data?.result?.itens) {
          setDowngradeElegibilidade(data.result);
          setShowDowngradeConfirm(false);
          setShowDowngradeInelegivel(true);
        } else {
          toast(data?.msg || 'Erro ao processar downgrade', { type: 'error', position: 'top-center', autoClose: 5000, toastId: 'downgrade-error' });
        }
      },
    }
  );

  const handleCnpjChange = (value: string) => {
    setCnpj(value);
    if (fieldErrors.cnpj) {
      setFieldErrors(prev => ({ ...prev, cnpj: '' }));
    }
    // Buscar dados automaticamente quando CNPJ for preenchido
    if (value.replace(/\D/g, '').length === 14) {
      buscarDadosCNPJ(value);
    } else {
      setDadosEndereco(null);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmailFinanceiro(value);
    if (fieldErrors.email) {
      setFieldErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const currencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
  const f = (n: number) => currencyFormatter.format(n);

  const queryPlanos = useQuery({
    queryKey: 'planos',
    queryFn: async () => {
      const response = await axios.get('/api/planos/box');
      return response.data;
    },
  });
  const planoFree = queryPlanos.data?.find((plano: any) => plano.nome === 'Free');
  const planoFreeItens = planoFree?.itens?.reduce((acc: any, item: any) => { acc[item.identificador] = item; return acc }, {});
  const valorPlanoFree = planoFree?.itens?.reduce((acc: number, item: any) => { return acc + (item.valor_unitario * item.capacidade) }, 0);

  const planoStart = queryPlanos.data?.find((plano: any) => plano.nome === 'Start');
  const planoStartItens = planoStart?.itens?.reduce((acc: any, item: any) => { acc[item.identificador] = item; return acc }, {});
  const valorPlanoStart = planoStart?.itens?.reduce((acc: number, item: any) => { return acc + (item.valor_unitario * item.capacidade) }, 0);

  const planoPro = queryPlanos.data?.find((plano: any) => plano.nome === 'Pro');
  const planoProItens = planoPro?.itens?.reduce((acc: any, item: any) => { acc[item.identificador] = item; return acc }, {});
  const valorPlanoPro = planoPro?.itens?.reduce((acc: number, item: any) => { return acc + (item.valor_unitario * item.capacidade) }, 0);

  const features = [
    {
      categoria: 'Capacidades',
      itens: [
        {
          nome: 'Usuários',
          icon: mdiAccountMultiple,
          descricao: 'Número de usuários que podem acessar o sistema',
          free: `${planoFreeItens?.usuarios?.capacidade ?? '2'} ${planoFreeItens?.usuarios?.capacidade == 1 ? 'usuário' : 'usuários'}`,
          start: `${planoStartItens?.usuarios?.capacidade ?? '2'} ${planoStartItens?.usuarios?.capacidade == 1 ? 'usuário' : 'usuários'}`,
          pro: `${planoProItens?.usuarios?.capacidade ?? '10'} ${planoProItens?.usuarios?.capacidade == 1 ? 'usuário' : 'usuários'}`,
          enterprise: 'Sob demanda/A combinar',
        },
        {
          nome: 'Canais',
          icon: mdiMessageText,
          descricao: 'Canais de comunicação (WhatsApp, Telegram, etc.)',
          free: `${planoFreeItens?.canais?.capacidade ?? '2'} ${planoFreeItens?.canais?.capacidade == 1 ? 'canal' : 'canais'}`,
          start: `${planoStartItens?.canais?.capacidade ?? '2'} ${planoStartItens?.canais?.capacidade == 1 ? 'canal' : 'canais'}`,
          pro: `${planoProItens?.canais?.capacidade ?? '5'} ${planoProItens?.canais?.capacidade == 1 ? 'canal' : 'canais'}`,
          enterprise: 'Sob demanda/A combinar',
        },
      ]
    },
    {
      categoria: 'Funcionalidades',
      itens: [
        {
          nome: 'Chat em tempo real',
          icon: mdiMessageText,
          descricao: 'Comunicação instantânea com clientes',
          free: true,
          pro: true,
          start: true,
          enterprise: true,
        },
        {
          nome: 'Gerenciamento de clientes',
          icon: mdiAccountMultiple,
          descricao: 'CRM completo para gestão de contatos',
          free: true,
          pro: true,
          start: true,
          enterprise: true,
        },
        {
          nome: 'Campanhas de mensagens',
          icon: mdiBell,
          descricao: 'Envio de mensagens em massa',
          free: false,
          pro: true,
          start: true,
          enterprise: true,
        },
        {
          nome: 'Notificações ilimitadas *',
          icon: mdiBell,
          descricao: 'Envio de notificações sem limites *',
          free: false,
          start: true,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'IA e Chatbots',
          icon: mdiRobot,
          descricao: 'Automação de atendimento com inteligência artificial',
          free: false,
          start: true,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'API de integração',
          icon: mdiApi,
          descricao: 'Integre o Terachat com seus sistemas',
          free: false,
          start: true,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'Relatórios avançados',
          icon: mdiChartLine,
          descricao: 'Dashboards e análises detalhadas',
          free: false,
          start: true,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'Funil de vendas',
          icon: mdiChartLine,
          descricao: 'Gestão completa do pipeline comercial',
          free: false,
          pro: true,
          start: true,
          enterprise: true,
        },
        {
          nome: 'Mensagens programadas',
          icon: mdiBell,
          descricao: 'Agende mensagens para envio automático',
          free: false,
          pro: true,
          start: true,
          enterprise: true,
        },
        {
          nome: 'Base de conhecimento',
          icon: mdiMessageText,
          descricao: 'Crie e gerencie artigos de ajuda',
          free: false,
          start: true,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'Integrações avançadas',
          icon: mdiApi,
          descricao: 'Conecte com múltiplas plataformas',
          free: false,
          start: true,
          pro: true,
          enterprise: true,
        },
      ],
      rodape: '* Notificações ilimitadas sujeitas a políticas de uso justo. Outros provedores de mensagens, como o WhatsApp, podem impor limites e/ou cobranças adicionais.'
    },
    {
      categoria: 'Suporte',
      itens: [
        {
          nome: 'Suporte por email',
          icon: mdiMessageText,
          descricao: 'Atendimento via email',
          free: true,
          start: true,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'Suporte prioritário',
          icon: mdiStarOutline,
          descricao: 'Atendimento com prioridade',
          free: false,
          start: false,
          pro: true,
          enterprise: true,
        },
        {
          nome: 'Onboarding dedicado',
          icon: mdiRocketLaunch,
          descricao: 'Treinamento personalizado da equipe',
          free: false,
          start: false,
          pro: false,
          enterprise: true,
        },
      ]
    }
  ];

  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon path={mdiCheck} size={1.5} color="var(--tc-color-success)" />
      ) : (
        <Icon path={mdiClose} size={1.5} color="var(--tc-color-gray-400)" />
      );
    }
    return <span className={styles.featureText}>{value}</span>;
  };

  if (queryPlanos.isLoading)
    return null

  return (
    <LayoutUsuario>
      <VBox gap="24px" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <VBox gap="8px" style={{ textAlign: 'center' }}>
          <Titulo>Upgrade de Plano</Titulo>
          <Descricao>
            Desbloqueie todo o potencial do Terachat com o plano Pro
          </Descricao>
        </VBox>

        {/* Cards de Planos */}
        <HBox gap="16px" style={{ marginTop: '24px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Plano Free */}
          <div style={{ flex: '1 1 200px', maxWidth: '400px', display: 'flex' }}>
            <Card className={styles.planCard} style={{ width: '100%' }}>
              <VBox gap="8px" style={{ padding: '24px' }}>
                {planoAtual === 'Free' && <div className={styles.planBadge}>Plano Atual</div>}
                {planoAtual !== 'Free' && (<div className={styles.planBadge}>
                  <Icon path={mdiCash} size={1} />
                  Grátis
                </div>)}
                <Subtitulo>Free</Subtitulo>
                <div className={styles.planPrice}>
                  <span className={styles.priceValue}>{f(valorPlanoFree)}</span>
                  <span className={styles.pricePeriod}>/mês</span>
                </div>
                <Descricao>
                  Ideal para testes e conhecer a plataforma
                </Descricao>
                <ul className={styles.planFeatures}>
                  <li>
                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                    {planoFreeItens?.usuarios?.capacidade ?? '2'} {planoFreeItens?.usuarios?.capacidade == 1 ? 'usuário' : 'usuários'}
                  </li>
                  <li>
                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                    {planoFreeItens?.canais?.capacidade ?? '2'} {planoFreeItens?.canais?.capacidade == 1 ? 'canal' : 'canais'} de comunicação
                  </li>
                  <li>
                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                    Funcionalidades básicas
                  </li>
                  <li>
                    <Icon path={mdiClose} size={1.2} color="var(--tc-color-gray-400)" />
                    Sem IA e automações
                  </li>
                </ul>
              </VBox>
            </Card>
          </div>

          {/* Plano Start */}
          {planoStart && (
            <div style={{ flex: '1 1 200px', maxWidth: '400px', display: 'flex' }}>
              <Card className={styles.planCard} style={{ width: '100%' }}>
                <VBox gap="8px" style={{ padding: '24px' }}>
                  {planoAtual === 'Start' && <div className={styles.planBadge}>Plano Atual</div>}
                  {planoAtual !== 'Start' && (<div className={styles.planBadge}>
                    <Icon path={mdiRocket} size={1} />
                    Crescimento
                  </div>)}
                  <Subtitulo>Start</Subtitulo>
                  <div className={styles.planPrice}>
                    <span className={styles.priceValue}>{f(valorPlanoStart)}</span>
                    <span className={styles.pricePeriod}>/mês</span>
                  </div>
                  <Descricao>
                    Ideal para testes e conhecer a plataforma
                  </Descricao>
                  <ul className={styles.planFeatures}>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      {planoStartItens?.usuarios?.capacidade ?? '2'} {planoStartItens?.usuarios?.capacidade == 1 ? 'usuário' : 'usuários'}  ({f(planoStartItens?.usuarios?.valor_excedente)}/usuário extra)
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      {planoStartItens?.canais?.capacidade ?? '2'} {planoStartItens?.canais?.capacidade == 1 ? 'canal' : 'canais'} ({f(planoStartItens?.canais?.valor_excedente)}/canal extra)
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      Todas as funcionalidades
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      IA e automações completas
                    </li>
                  </ul>
                  {planoAtual !== 'Start' && (
                    <Botao
                      variant="primary"
                      icon={mdiRocketLaunch}
                      onClick={() => handleUpgradeClick('start')}
                      style={{ marginTop: 'auto' }}
                    >
                      Contratar Agora
                    </Botao>
                  )}
                </VBox>
              </Card>
            </div>
          )}

          {/* Plano Pro */}
          {planoPro && (
            <div style={{ flex: '1 1 200px', maxWidth: '400px', display: 'flex' }}>
              <Card className={`${styles.planCard} ${styles.planCardHighlight}`} style={{ width: '100%' }}>
                <VBox gap="8px" style={{ padding: '24px' }}>
                  {planoAtual === 'Pro' && <div className={styles.planBadge}>Plano Atual</div>}
                  {planoAtual !== 'Pro' && (<div className={styles.planBadgeRecommended}>
                    <Icon path={mdiStarOutline} size={1} />
                    Recomendado
                  </div>)}
                  <Subtitulo>Pro</Subtitulo>
                  <div className={styles.planPrice}>
                    <span className={styles.priceValueHighlight}>{f(valorPlanoPro)}</span>
                    <span className={styles.pricePeriod}>/mês</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                    + usuários e canais adicionais
                  </div>
                  <Descricao>
                    Solução completa para profissionais e empresas
                  </Descricao>
                  <ul className={styles.planFeatures}>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      {planoProItens?.usuarios?.capacidade ?? '10'} {planoProItens?.usuarios?.capacidade == 1 ? 'usuário' : 'usuários'} ({f(planoProItens?.usuarios?.valor_excedente)}/usuário extra)
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      {planoProItens?.canais?.capacidade ?? '5'} {planoProItens?.canais?.capacidade == 1 ? 'canal' : 'canais'} ({f(planoProItens?.canais?.valor_excedente)}/canal extra)
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      Todas as funcionalidades
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      IA e automações completas
                    </li>
                    <li>
                      <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                      Suporte prioritário
                    </li>
                  </ul>
                  {planoAtual !== 'Pro' && (
                    <Botao
                      variant="primary"
                      icon={mdiRocketLaunch}
                      onClick={() => handleUpgradeClick('pro')}
                      style={{ marginTop: 'auto' }}
                    >
                      Contratar Agora
                    </Botao>
                  )}
                </VBox>
              </Card>
            </div>
          )}

          {/* Plano Enterprise */}
          <div style={{ flex: '1 1 200px', maxWidth: '400px', display: 'flex' }}>
            <Card className={`${styles.planCard}`} style={{ width: '100%' }}>
              <VBox gap="8px" style={{ padding: '24px' }}>
                {planoAtual === 'Enterprise' && <div className={styles.planBadge}>Plano Atual</div>}
                {planoAtual !== 'Enterprise' && (
                  <HBox className={styles.planBadge} gap={12}>
                    <Icon path={mdiOfficeBuilding} size={1} />
                    <span>Para empresas</span>
                  </HBox>
                )}
                <Subtitulo>Enterprise</Subtitulo>
                <div className={styles.planPrice}>
                  <span className={styles.priceValue}>{planoAtual === 'Enterprise' ? 'Sob demanda' : 'Consulte'}</span>
                  <span className={styles.pricePeriod}>/mês</span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                  + usuários e canais adicionais
                </div>
                <Descricao>
                  Solução completa para profissionais e empresas
                </Descricao>
                <ul className={styles.planFeatures}>
                  <li>
                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                    Todas as funcionalidades
                  </li>
                  <li>
                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                    IA e automações completas
                  </li>
                  <li>
                    <Icon path={mdiCheck} size={1.2} color="var(--tc-color-success)" />
                    Suporte prioritário
                  </li>
                </ul>
                {planoAtual !== 'Enterprise' && (
                  <Botao
                    variant="primary"
                    icon={mdiRocketLaunch}
                    onClick={() => handleUpgradeClick('enterprise')}
                    style={{ marginTop: 'auto' }}
                  >
                    Solicitar Orçamento
                  </Botao>
                )}
              </VBox>
            </Card>
          </div>
        </HBox>

        {/* Botão de downgrade para Free */}
        {planoAtual !== 'Free' && (
          <div style={{ textAlign: 'center' }}>
            <Botao
              variant="outline-secondary"
              icon={isVerificandoElegibilidade ? mdiLoading : mdiTrendingDown}
              onClick={handleDowngradeFreeClick}
              disabled={isVerificandoElegibilidade}
              style={{ fontSize: '13px', color: 'var(--tc-color-gray-500)' }}
            >
              {isVerificandoElegibilidade ? 'Verificando...' : 'Voltar ao plano Free'}
            </Botao>
          </div>
        )}

        <Grid>
          {/* Tabela Comparativa */}
          <GridItem sm={12}>
            <VBox gap="16px" style={{ marginTop: '48px', width: '100%' }}>
              <Subtitulo>Comparativo Completo de Funcionalidades</Subtitulo>

              {features.map((categoria) => (
                <Card key={categoria.categoria} style={{ marginBottom: '24px', width: '100%' }}>
                  <div className={styles.categoryHeader}>
                    <h3>{categoria.categoria}</h3>
                  </div>
                  <div className={styles.comparisonTable}>
                    <div className={styles.tableHeader}>
                      <div className={styles.featureColumn}>Funcionalidade</div>
                      <div className={styles.planColumn}>Free</div>
                      {planoStart && (<div className={styles.planColumn}>Start</div>)}
                      {planoPro && (<div className={styles.planColumn}>Pro</div>)}
                      <div className={styles.planColumn}>Enterprise</div>
                    </div>
                    {categoria.itens.map((item, index) => (
                      <div key={index} className={styles.tableRow}>
                        <div className={styles.featureColumn}>
                          <HBox gap="12px">
                            <Icon path={item.icon} size={1.2} color="var(--tc-color-primary)" />
                            <VBox gap="4px">
                              <span className={styles.featureName}>{item.nome}</span>
                              <span className={styles.featureDescription}>{item.descricao}</span>
                            </VBox>
                          </HBox>
                        </div>
                        <div className={styles.planColumn}>
                          {renderFeatureValue(item.free)}
                        </div>
                        {planoStart && (
                          <div className={styles.planColumn}>
                            {renderFeatureValue(item.start)}
                          </div>
                        )}
                        {planoPro && (
                          <div className={styles.planColumn}>
                            {renderFeatureValue(item.pro)}
                          </div>
                        )}
                        <div className={styles.planColumn}>
                          {renderFeatureValue(item.enterprise)}
                        </div>
                      </div>
                    ))}
                    {categoria.rodape && (<div className={styles.tableFooter}>
                      {categoria.rodape}
                    </div>
                    )}
                  </div>
                </Card>
              ))}
            </VBox>
          </GridItem>
          <GridItem sm={12}>
            {/* CTA Final */}
            {planoPro && (
              <Card style={{ marginTop: '32px', background: 'linear-gradient(135deg, var(--tc-color-primary) 0%, var(--tc-color-primary-800) 100%)' }}>
                <VBox gap="16px" center style={{ padding: '48px 24px', textAlign: 'center' }}>
                  <Icon path={mdiRocketLaunch} size={3} color="white" />
                  <h2 style={{ fontSize: '28px', margin: 0, color: 'white' }}>Pronto para crescer?</h2>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '600px' }}>
                    Faça o upgrade agora e tenha acesso a todas as funcionalidades para levar seu atendimento ao próximo nível
                  </div>
                  <Botao
                    variant="secondary"
                    icon={mdiRocketLaunch}
                    onClick={() => handleUpgradeClick('pro')}
                    style={{
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    Fazer Upgrade para Pro
                  </Botao>
                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', marginTop: '16px' }}>
                    Dúvidas? Entre em contato com nossa equipe de vendas
                  </div>
                </VBox>
              </Card>
            )}
          </GridItem>
        </Grid>
      </VBox>

      {/* Modal de Upgrade */}
      <Modal
        show={upgradeTo !== null}
        onClose={() => setUpgradeTo(null)}
        onCancel={() => setUpgradeTo(null)}
        onConfirm={handleConfirmUpgrade}
        title={upgradeTo === 'enterprise' ? 'Solicitar orçamento Enterprise' : `Ativar plano ${upgradeToLabel}`}
        size="md"
        confirmProps={{
          label: upgradeTo === 'enterprise' ? 'Solicitar Orçamento' : 'Continuar',
          disabled: upgradeMutation.isLoading,
        }}
        cancelProps={{
          label: 'Cancelar',
          disabled: upgradeMutation.isLoading,
        }}
      >
        <VBox gap="16px">
          <Descricao>
            {upgradeTo === 'enterprise'
              ? 'O plano Enterprise é personalizado para grandes operações. Preencha seus dados e nossa equipe comercial entrará em contato para apresentar uma proposta.'
              : `Para ativar o plano ${upgradeToLabel}, precisamos dos dados para contrato e faturamento.`}
          </Descricao>

          <Grid>
            <GridItem sm={12}>
              <CampoTexto
                label="E-mail financeiro"
                value={emailFinanceiro}
                onChange={handleEmailChange}
                placeholder="financeiro@empresa.com"
                error={fieldErrors.email}
                type="email"
                disabled={upgradeMutation.isLoading}
              />
            </GridItem>

            <GridItem sm={12}>
              <VBox gap="4px">
                <HBox gap="8px" style={{ alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <CampoMascara
                      label="CNPJ da empresa"
                      value={cnpj}
                      onChange={handleCnpjChange}
                      masks={["99.999.999/9999-99"]}
                      error={fieldErrors.cnpj}
                      disabled={upgradeMutation.isLoading}
                    />
                  </div>
                  <Botao
                    variant="outline-primary"
                    icon={isBuscandoCNPJ ? mdiLoading : mdiMagnify}
                    onClick={() => buscarDadosCNPJ(cnpj)}
                    disabled={isBuscandoCNPJ || upgradeMutation.isLoading || !cnpj || cnpj.replace(/\D/g, '').length !== 14}
                    style={{ marginBottom: fieldErrors.cnpj ? '24px' : '0' }}
                  >
                    {isBuscandoCNPJ ? 'Buscando...' : 'Buscar'}
                  </Botao>
                </HBox>
              </VBox>
            </GridItem>

            {dadosEndereco && (
              <GridItem sm={12}>
                <AlertBox variant="info" style={{ marginTop: '8px' }}>
                  <VBox gap="4px">
                    <strong>Dados encontrados:</strong>
                    <div><strong>Razão Social:</strong> {dadosEndereco.razao_social}</div>
                    <div><strong>Nome Fantasia:</strong> {dadosEndereco.nome_fantasia}</div>
                    {dadosEndereco.endereco_logradouro && (
                      <>
                        <div style={{ marginTop: '8px' }}><strong>Endereço:</strong></div>
                        <div>
                          {dadosEndereco.endereco_logradouro}
                          {dadosEndereco.endereco_numero && `, ${dadosEndereco.endereco_numero}`}
                          {dadosEndereco.endereco_complemento && ` - ${dadosEndereco.endereco_complemento}`}
                        </div>
                        <div>
                          {dadosEndereco.endereco_bairro}
                          {dadosEndereco.endereco_cidade && ` - ${dadosEndereco.endereco_cidade}`}
                          {dadosEndereco.endereco_estado && `/${dadosEndereco.endereco_estado}`}
                        </div>
                        <div>CEP: {dadosEndereco.endereco_cep}</div>
                      </>
                    )}
                  </VBox>
                </AlertBox>
              </GridItem>
            )}

            {upgradeTo !== 'enterprise' && (
              <GridItem sm={12}>
                <AlertBox variant="info">
                  <HBox gap="8px" style={{ alignItems: 'flex-start' }}>
                    <Icon path={mdiInformationOutline} size={1} />
                    <div>
                      <strong>💡 Dica:</strong> Pagamento via PIX libera o acesso imediatamente. Via boleto pode levar até 2 dias úteis.
                    </div>
                  </HBox>
                </AlertBox>
              </GridItem>
            )}
          </Grid>
        </VBox>
      </Modal>

      {/* Modal de confirmação de downgrade para Free */}
      <Modal
        show={showDowngradeConfirm}
        onClose={() => setShowDowngradeConfirm(false)}
        onCancel={() => setShowDowngradeConfirm(false)}
        onConfirm={() => downgradeMutation.mutate()}
        title="Voltar ao plano Free"
        size="md"
        confirmProps={{ label: 'Confirmar downgrade', disabled: downgradeMutation.isLoading }}
        cancelProps={{ label: 'Cancelar', disabled: downgradeMutation.isLoading }}
      >
        <VBox gap="16px">
          <AlertBox variant="warning">
            <HBox gap="8px" style={{ alignItems: 'flex-start' }}>
              <Icon path={mdiAlertCircleOutline} size={1} />
              <div>
                <strong>Atenção!</strong> Ao voltar para o plano Free, seu ambiente passará a ter as limitações do plano gratuito. Funcionalidades como IA, automações avançadas e canais adicionais poderão ficar indisponíveis.
              </div>
            </HBox>
          </AlertBox>
          <Descricao>Tem certeza que deseja voltar ao plano Free?</Descricao>
        </VBox>
      </Modal>

      {/* Modal de inelegibilidade para downgrade Free */}
      <Modal
        show={showDowngradeInelegivel}
        onClose={() => setShowDowngradeInelegivel(false)}
        onCancel={() => setShowDowngradeInelegivel(false)}
        onConfirm={() => setShowDowngradeInelegivel(false)}
        title="Não é possível voltar ao plano Free"
        size="md"
        confirmProps={{ label: 'Entendi' }}
      >
        <VBox gap="16px">
          <Descricao>
            Para voltar ao plano Free, seu ambiente precisa estar dentro dos limites do plano gratuito.
            Reduza o uso dos itens abaixo e tente novamente:
          </Descricao>
          {downgradeElegibilidade?.itens?.length > 0 && (
            <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--tc-color-gray-200)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: 'var(--tc-color-gray-50)', borderBottom: '1px solid var(--tc-color-gray-200)' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--tc-color-gray-700)' }}>Item</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: 'var(--tc-color-gray-700)' }}>Atual</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: 'var(--tc-color-gray-700)' }}>Limite Free</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: 'var(--tc-color-gray-700)' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {downgradeElegibilidade.itens.map((item: any) => (
                    <tr key={item.identificador} style={{ borderBottom: '1px solid var(--tc-color-gray-100)' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 500 }}>{item.label}</td>
                      <td style={{
                        padding: '10px 16px',
                        textAlign: 'center',
                        color: item.elegivel ? 'inherit' : 'var(--tc-color-danger)',
                        fontWeight: item.elegivel ? 'normal' : 600,
                      }}>
                        {item.atual}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>{item.limite}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        {item.elegivel
                          ? <Icon path={mdiCheck} size={1} color="var(--tc-color-success)" />
                          : <Icon path={mdiClose} size={1} color="var(--tc-color-danger)" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VBox>
      </Modal>

    </LayoutUsuario>
  );
};

export default Page;
